import React, { useState, useEffect } from 'react';
import { API_URLS } from '../../../config/api';
import { adminApiFetch } from '../../../utils/adminApi';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff,
  Layout,
  Code,
  Globe,
  Palette,
  Home,
  Monitor,
  Smartphone,
  Briefcase,
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';

const AdminWebsiteTemplateCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parent_id: null,
    icon: 'Layout',
    color: '#6B7280',
    sort_order: 0,
    is_active: 1
  });
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [viewMode, setViewMode] = useState('tree'); // 'tree' or 'grid'

  const iconOptions = [
    { value: 'Layout', label: 'Layout', icon: Layout },
    { value: 'Code', label: 'Code', icon: Code },
    { value: 'Globe', label: 'Globe', icon: Globe },
    { value: 'Palette', label: 'Palette', icon: Palette },
    { value: 'Home', label: 'Home', icon: Home },
    { value: 'Monitor', label: 'Monitor', icon: Monitor },
    { value: 'Smartphone', label: 'Smartphone', icon: Smartphone },
    { value: 'Briefcase', label: 'Briefcase', icon: Briefcase }
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await adminApiFetch(API_URLS.ADMIN.WEBSITE_TEMPLATE_CATEGORIES);
      const result = await response.json();
      if (result.status === 'success') {
        setCategories(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Build hierarchical structure
  const buildCategoryTree = (categories) => {
    const categoryMap = {};
    const rootCategories = [];
    
    // Create map
    categories.forEach(cat => {
      categoryMap[cat.id] = { ...cat, children: [] };
    });
    
    // Build tree
    categories.forEach(cat => {
      if (cat.parent_id && categoryMap[cat.parent_id]) {
        categoryMap[cat.parent_id].children.push(categoryMap[cat.id]);
      } else {
        rootCategories.push(categoryMap[cat.id]);
      }
    });
    
    return rootCategories;
  };
  
  const categoryTree = buildCategoryTree(categories);
  
  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };
  
  // Get all categories for parent selection (excluding current category and its descendants)
  const getAvailableParents = (currentCategoryId = null) => {
    if (!currentCategoryId) {
      return categories.filter(cat => !cat.parent_id);
    }
    
    // Get all descendants to exclude
    const getDescendants = (parentId) => {
      const descendants = [];
      const children = categories.filter(cat => cat.parent_id === parentId);
      children.forEach(child => {
        descendants.push(child.id);
        descendants.push(...getDescendants(child.id));
      });
      return descendants;
    };
    
    const excludeIds = new Set([currentCategoryId, ...getDescendants(currentCategoryId)]);
    return categories.filter(cat => !excludeIds.has(cat.id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = `${API_URLS.ADMIN.ADMIN_API}?action=${editingCategory ? 'updateWebsiteTemplateCategory&id=' + editingCategory.id : 'createWebsiteTemplateCategory'}`;
      
      const method = 'POST';
      
      const response = await adminApiFetch(url, {
        method,
        body: JSON.stringify(editingCategory ? { ...formData, id: editingCategory.id } : formData),
      });

      const result = await response.json();
      if (result.status === 'success') {
        fetchCategories();
        setShowModal(false);
        setEditingCategory(null);
        setFormData({
          name: '',
          description: '',
          parent_id: null,
          icon: 'Layout',
          color: '#6B7280',
          sort_order: 0,
          is_active: 1
        });
      } else {
        alert('Error: ' + result.message);
      }
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Error saving category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      parent_id: category.parent_id || null,
      icon: category.icon || 'Layout',
      color: category.color || '#6B7280',
      sort_order: category.sort_order || 0,
      is_active: category.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (categoryId) => {
    if (window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz?')) {
      try {
        const response = await adminApiFetch(`${API_URLS.ADMIN.ADMIN_API}?action=deleteWebsiteTemplateCategory&id=${categoryId}`);
        const result = await response.json();
        if (result.status === 'success') {
          fetchCategories();
        } else {
          alert('Error: ' + result.message);
        }
      } catch (error) {
        console.error('Error deleting category:', error);
        alert('Error deleting category');
      }
    }
  };

  const handleToggleStatus = async (categoryId) => {
    try {
      // Toggle status by updating the category
      const category = categories.find(c => c.id === categoryId);
      if (category) {
        const url = `${API_URLS.ADMIN.ADMIN_API}?action=updateWebsiteTemplateCategory&id=${categoryId}`;
        const response = await adminApiFetch(url, {
          method: 'POST',
          body: JSON.stringify({
            ...category,
            is_active: category.is_active ? 0 : 1
          }),
        });
        const result = await response.json();
        if (result.status === 'success') {
          fetchCategories();
        } else {
          alert('Error: ' + result.message);
        }
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Error toggling status');
    }
  };

  const getIconComponent = (iconName) => {
    const iconOption = iconOptions.find(option => option.value === iconName);
    return iconOption ? iconOption.icon : Layout;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Render category tree recursively
  const renderCategoryTree = (category, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedCategories.has(category.id);
    const IconComponent = getIconComponent(category.icon);
    const parentCategory = categories.find(c => c.id === category.parent_id);
    
    return (
      <div key={category.id} className="mb-2">
        <Card className={`hover:shadow-lg transition-shadow ${category.parent_id ? 'ml-6 border-l-2' : ''}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 flex-1">
                {hasChildren && (
                  <button
                    onClick={() => toggleCategory(category.id)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                )}
                {!hasChildren && <div className="w-6" />}
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: category.color + '20', color: category.color }}
                >
                  <IconComponent className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-lg">{category.name}</CardTitle>
                    {parentCategory && (
                      <Badge variant="outline" className="text-xs">
                        {parentCategory.name}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 truncate">{category.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={category.is_active ? "default" : "secondary"}>
                  {category.is_active ? 'Aktif' : 'Pasif'}
                </Badge>
                <div className="flex space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleToggleStatus(category.id)}
                  >
                    {category.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(category)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(category.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-gray-600">
              Sıra: {category.sort_order}
            </div>
          </CardContent>
        </Card>
        {hasChildren && isExpanded && (
          <div className="mt-2">
            {category.children.map(child => renderCategoryTree(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Kolay Site Tasarım Kategorileri</h1>
        <div className="flex gap-2">
          <Button 
            variant={viewMode === 'tree' ? 'default' : 'outline'}
            onClick={() => setViewMode('tree')}
          >
            <FolderOpen className="w-4 h-4 mr-2" />
            Ağaç Görünümü
          </Button>
          <Button 
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            onClick={() => setViewMode('grid')}
          >
            <Folder className="w-4 h-4 mr-2" />
            Grid Görünümü
          </Button>
          <Button onClick={() => setShowModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Yeni Kategori
          </Button>
        </div>
      </div>

      {viewMode === 'tree' ? (
        <div className="space-y-2">
          {categoryTree.length > 0 ? (
            categoryTree.map(category => renderCategoryTree(category))
          ) : (
            <p className="text-center text-gray-500 py-8">Henüz kategori eklenmemiş</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category) => {
            const IconComponent = getIconComponent(category.icon);
            const parentCategory = categories.find(c => c.id === category.parent_id);
            return (
              <Card key={category.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: category.color + '20', color: category.color }}
                      >
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-lg">{category.name}</CardTitle>
                          {parentCategory && (
                            <Badge variant="outline" className="text-xs">
                              {parentCategory.name}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{category.description}</p>
                      </div>
                    </div>
                    <Badge variant={category.is_active ? "default" : "secondary"}>
                      {category.is_active ? 'Aktif' : 'Pasif'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      Sıra: {category.sort_order}
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleToggleStatus(category.id)}
                      >
                        {category.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(category)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(category.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingCategory ? 'Kategori Düzenle' : 'Yeni Kategori'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Kategori Adı</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Açıklama</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="parent_id">Üst Kategori</Label>
                <Select 
                  value={formData.parent_id || '__none__'} 
                  onValueChange={(value) => setFormData({ ...formData, parent_id: value === '__none__' ? null : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Üst kategori seçin (opsiyonel)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Yok (Ana Kategori)</SelectItem>
                    {getAvailableParents(editingCategory?.id).map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="icon">İkon</Label>
                <Select value={formData.icon} onValueChange={(value) => setFormData({ ...formData, icon: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {iconOptions.map((option) => {
                      const IconComponent = option.icon;
                      return (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center space-x-2">
                            <IconComponent className="w-4 h-4" />
                            <span>{option.label}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="color">Renk</Label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    id="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    placeholder="#6B7280"
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="sort_order">Sıra</Label>
                <Input
                  id="sort_order"
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                />
                <Label htmlFor="is_active">Aktif</Label>
              </div>
              <div className="flex space-x-2">
                <Button type="submit" className="flex-1">
                  {editingCategory ? 'Güncelle' : 'Oluştur'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCategory(null);
                    setFormData({
                      name: '',
                      description: '',
                      parent_id: null,
                      icon: 'Layout',
                      color: '#6B7280',
                      sort_order: 0,
                      is_active: 1
                    });
                  }}
                >
                  İptal
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWebsiteTemplateCategories;
